import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Header } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';
import {
  Users,
  Building2,
  CreditCard,
  Loader2,
  Search,
  Edit,
  Calendar,
  Lock,
  Unlock,
  AlertTriangle,
  Trash2,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState({
    subscription_tier: 'basic',
    subscription_months: 1,
  });
  const [lockReason, setLockReason] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSubscriptionDialog = (user) => {
    setSelectedUser(user);
    setSubscriptionData({
      subscription_tier: user.subscription_tier || 'basic',
      subscription_months: 1,
    });
    setSubscriptionDialogOpen(true);
  };

  const handleOpenLockDialog = (user) => {
    setSelectedUser(user);
    setLockReason(user.is_locked ? '' : 'Payment is due. Please contact support to reactivate your account.');
    setLockDialogOpen(true);
  };

  const handleToggleLock = async () => {
    if (!selectedUser) return;
    setSaving(true);

    try {
      const newLockState = !selectedUser.is_locked;
      await axios.put(`${API_URL}/admin/users/${selectedUser.id}/lock`, {
        is_locked: newLockState,
        lock_reason: newLockState ? lockReason : null,
      });
      
      toast.success(`Account ${newLockState ? 'locked' : 'unlocked'} successfully`);
      setLockDialogOpen(false);
      setLockReason('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update account lock status');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSubscription = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API_URL}/admin/users/${selectedUser.id}/subscription`, subscriptionData);
      toast.success('Subscription updated successfully');
      setSubscriptionDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update subscription');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setDeleting(true);

    try {
      await axios.delete(`${API_URL}/admin/users/${selectedUser.id}`);
      toast.success(`${selectedUser.name} and all associated data deleted`);
      setDeleteDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const getSubscriptionBadgeColor = (tier) => {
    switch (tier) {
      case 'cloud':
        return 'bg-purple-100 text-purple-800';
      case 'basic':
        return 'bg-blue-100 text-blue-800';
      case 'trial':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.company_name && user.company_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header
        title="User Management"
        subtitle={`${users.length} registered users`}
      />

      <div className="flex-1 p-4 lg:p-8 space-y-6">
        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Users List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              All Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">User</th>
                    <th className="text-left py-3 px-4">Company</th>
                    <th className="text-left py-3 px-4">Role</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-left py-3 px-4">Subscription</th>
                    <th className="text-left py-3 px-4">Expires</th>
                    <th className="text-left py-3 px-4">Created</th>
                    <th className="text-right py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className={`border-b hover:bg-muted/30 ${user.is_locked ? 'bg-red-50' : ''}`}>
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-muted-foreground" />
                          <span>{user.company_name || user.tenant_name || '-'}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant={user.role === 'admin' ? 'destructive' : 'secondary'}>
                          {user.role}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        {user.is_locked ? (
                          <Badge variant="destructive" className="flex items-center gap-1 w-fit">
                            <Lock className="w-3 h-3" />
                            Locked
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="flex items-center gap-1 w-fit text-green-600 border-green-200">
                            <Unlock className="w-3 h-3" />
                            Active
                          </Badge>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSubscriptionBadgeColor(user.subscription_tier)}`}>
                          {user.subscription_tier?.toUpperCase() || 'TRIAL'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          {user.subscription_tier === 'trial' && user.trial_ends ? (
                            <span className={new Date(user.trial_ends) < new Date() ? 'text-destructive' : ''}>
                              {formatDate(user.trial_ends)}
                            </span>
                          ) : user.subscription_expires ? (
                            <span className={new Date(user.subscription_expires) < new Date() ? 'text-destructive' : ''}>
                              {formatDate(user.subscription_expires)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {user.role !== 'admin' && (
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant={user.is_locked ? "destructive" : "outline"}
                              onClick={() => handleOpenLockDialog(user)}
                              className="gap-1"
                              title={user.is_locked ? 'Unlock Account' : 'Lock Account'}
                            >
                              {user.is_locked ? (
                                <Unlock className="w-4 h-4" />
                              ) : (
                                <Lock className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenSubscriptionDialog(user)}
                              className="gap-1"
                            >
                              <CreditCard className="w-4 h-4" />
                              Manage
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenDeleteDialog(user)}
                              className="gap-1 text-destructive hover:text-destructive hover:bg-destructive/10"
                              title="Delete user and all data"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredUsers.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No users found matching your search.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Lock/Unlock Account Dialog */}
      <AlertDialog open={lockDialogOpen} onOpenChange={setLockDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {selectedUser?.is_locked ? (
                <>
                  <Unlock className="w-5 h-5 text-green-600" />
                  Unlock Account
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5 text-destructive" />
                  Lock Account
                </>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectedUser?.is_locked ? (
                <span>
                  Are you sure you want to unlock <strong>{selectedUser?.name}</strong>'s account?
                  They will be able to login again.
                </span>
              ) : (
                <div className="space-y-3">
                  <span>
                    Are you sure you want to lock <strong>{selectedUser?.name}</strong>'s account?
                    They will not be able to login until unlocked.
                  </span>
                  <div className="mt-4">
                    <Label htmlFor="lock_reason">Lock Reason (shown to user on login)</Label>
                    <Textarea
                      id="lock_reason"
                      value={lockReason}
                      onChange={(e) => setLockReason(e.target.value)}
                      placeholder="Payment is due. Please contact support..."
                      className="mt-2"
                      rows={3}
                    />
                  </div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleToggleLock}
              disabled={saving}
              className={selectedUser?.is_locked ? 'bg-green-600 hover:bg-green-700' : 'bg-destructive hover:bg-destructive/90'}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : selectedUser?.is_locked ? (
                'Unlock Account'
              ) : (
                'Lock Account'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Subscription Dialog */}
      <Dialog open={subscriptionDialogOpen} onOpenChange={setSubscriptionDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Update Subscription</DialogTitle>
            <DialogDescription>
              {selectedUser && (
                <span>
                  Managing subscription for <strong>{selectedUser.name}</strong> ({selectedUser.email})
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateSubscription}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="subscription_tier">Subscription Tier</Label>
                <Select
                  value={subscriptionData.subscription_tier}
                  onValueChange={(value) => setSubscriptionData({ ...subscriptionData, subscription_tier: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select tier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial (7 days)</SelectItem>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="cloud">Cloud (Premium)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {subscriptionData.subscription_tier !== 'trial' && (
                <div className="space-y-2">
                  <Label htmlFor="subscription_months">Duration (Months)</Label>
                  <Select
                    value={subscriptionData.subscription_months.toString()}
                    onValueChange={(value) => setSubscriptionData({ ...subscriptionData, subscription_months: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 Month</SelectItem>
                      <SelectItem value="3">3 Months</SelectItem>
                      <SelectItem value="6">6 Months</SelectItem>
                      <SelectItem value="12">12 Months (1 Year)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="p-4 bg-muted/30 rounded-lg">
                <h4 className="font-medium mb-2">Subscription Details:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {subscriptionData.subscription_tier === 'trial' && (
                    <>
                      <li>• 7-day free trial</li>
                      <li>• Full access to all features</li>
                      <li>• Auto-prompts to subscribe after trial</li>
                    </>
                  )}
                  {subscriptionData.subscription_tier === 'basic' && (
                    <>
                      <li>• Standard payroll features</li>
                      <li>• Payrolls auto-delete after 3 weeks if draft</li>
                      <li>• Limited storage</li>
                    </>
                  )}
                  {subscriptionData.subscription_tier === 'cloud' && (
                    <>
                      <li>• All payroll features</li>
                      <li>• Unlimited payroll storage</li>
                      <li>• Priority support</li>
                      <li>• Advanced reports</li>
                    </>
                  )}
                </ul>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSubscriptionDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Subscription'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Delete User
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Are you sure you want to permanently delete <strong>{selectedUser?.name}</strong> ({selectedUser?.email})?
                </p>
                <div className="p-3 bg-destructive/10 rounded-lg border border-destructive/20 text-sm">
                  <p className="font-medium text-destructive mb-1">This will permanently delete:</p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
                    <li>The user account</li>
                    <li>Their farm group (tenant)</li>
                    <li>All workers, truckers, and owners</li>
                    <li>All payroll periods and entries</li>
                    <li>All rates and audit logs</li>
                  </ul>
                </div>
                <p className="text-sm font-medium text-destructive">This action cannot be undone.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              disabled={deleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Permanently'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
